import { computed, reactive, ref, Ref } from 'vue';
import { useInfiniteQuery } from 'vue-query';
import { UseInfiniteQueryOptions } from 'react-query/types';
import { flatten } from 'lodash';
import QUERY_KEYS from '@/constants/queryKeys';
import { POOLS } from '@/constants/pools';
import { balancerFarmSubgraphService } from '@/services/balancer/subgraph/balancer-farm-subgraph.service';
import { balancerSubgraphService } from '@/services/balancer/subgraph/balancer-subgraph.service';
import { DecoratedPool } from '@/services/balancer/subgraph/types';
import { forChange } from '@/lib/utils';
import useTokens from '../useTokens';
import useUserSettings from '../useUserSettings';
import useApp from '../useApp';
import useNetwork from '../useNetwork';
import useWeb3 from '@/services/web3/useWeb3';

type PoolsQueryResponse = {
  pools: DecoratedPool[];
  tokens: string[];
  skip?: number;
  enabled?: boolean;
};

type FilterOptions = {
  poolIds?: Ref<string[]>;
  isExactTokensList?: boolean;
  pageSize?: number;
};

export default function useFarmPoolsQuery(
  tokenList: Ref<string[]> = ref([]),
  options: UseInfiniteQueryOptions<PoolsQueryResponse> = {},
  filterOptions?: FilterOptions
) {
  console.log('tokenList', tokenList);
  // COMPOSABLES
  const { injectTokens, dynamicDataLoading, prices } = useTokens();
  const { currency } = useUserSettings();
  const { appLoading } = useApp();
  const { networkId } = useNetwork();
  const { appNetworkConfig } = useWeb3();
  // DATA
  const queryKey = QUERY_KEYS.FarmPools.All(networkId);

  // COMPUTED
  const enabled = computed(() => !appLoading.value && options.enabled);

  const queryFn = async ({ pageParam = 0 }) => {
    const queryArgs: any = {
      where: {
        pid_not_in: [0]
      }
    };
    if (filterOptions?.poolIds?.value.length) {
      queryArgs.where.id_in = filterOptions.poolIds.value;
    }
    const farmingInfo = await balancerFarmSubgraphService.farmingInfo.get({
      id: '1'
    });
    const pools = await balancerFarmSubgraphService.farmPools.get(queryArgs);
    const investPools = await balancerSubgraphService.pools.get({});
    pools.forEach(i => {
      const singleFarm = POOLS.SingleFarm[networkId.value];

      const investPool = investPools.find(j => j.address === i.lpToken);
      if (investPool) {
        Object.assign(i, investPool, { isSingleFarm: false });
      } else {
        const farmInfo = singleFarm[`${i.lpToken}_${i.pid}`];
        Object.assign(i, { isSingleFarm: true }, farmInfo);
      }
    });

    const tokens = flatten(pools.map(pool => [pool.lpToken]));
    await injectTokens(tokens);
    await forChange(dynamicDataLoading, false);

    const decoratedPools = await balancerFarmSubgraphService.farmPools.decorate(
      pools,
      '24h',
      prices.value,
      currency.value,
      farmingInfo,
      appNetworkConfig.addresses.flake
    );

    return {
      pools: decoratedPools,
      tokens,
      skip:
        pools.length >= POOLS.Pagination.PerPage
          ? pageParam + POOLS.Pagination.PerPage
          : undefined
    };
  };

  const queryOptions = reactive({
    enabled,
    getNextPageParam: (lastPage: PoolsQueryResponse) => lastPage.skip,
    ...options
  });

  return useInfiniteQuery(queryKey, queryFn, queryOptions);
}
